import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../auth/AuthContext";
import adminMenu from "../../source/menuAdmin.json";
import guestMenuEn from "../../locales/en/menuGuest.json";
import guestMenuEs from "../../locales/es/menuGuest.json";
import guestMenuSv from "../../locales/sv/menuGuest.json";
import guestMenuDe from "../../locales/de/menuGuest.json";
import i18n from "i18next";
import UtilMethods from "../../util/UtilMethods";
import MarianLogo from "./MarianLogo"; // Import the MarianLogo component
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes, faGlobe } from "@fortawesome/free-solid-svg-icons"; // Import icons
import { LANGUAGES } from "./Constants";
import { getPublicActiveEvents } from "../../services/EventMasterService";

const mapEventFromDb = (event) => ({
  id: event?.eventID,
  name: event?.name || "",
  reg_date: event?.regDate || "",
  reg_end_date: event?.regEndDate || "",
  start_date: event?.startDate || "",
  reg_type: event?.onlineRegRequired ? "YES" : "NO",
});

const MenuBar = () => {
  const { user, loading, logout, userType } = useAuth();
  const [events, setEvents] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [activeRetreat, setActiveRetreat] = useState(false);
  const [language, setLanguage] = useState(i18n.language.slice(0, 2));
  const [isExpanded, setIsExpanded] = useState(false); // State to track mobile menu
  const getActiveRetreat = useCallback(() => {
    const activeEvent = events.find(
      (event) =>
        UtilMethods.canRegister(
          event.reg_date,
          event.reg_end_date || event.start_date
        ) && event.reg_type !== "NO"
    );
    setActiveRetreat(activeEvent);
  }, [events]);

  useEffect(() => {
    let cancelled = false;

    const loadEvents = async () => {
      try {
        const response = await getPublicActiveEvents();
        const dbEvents = Array.isArray(response?.events)
          ? response.events.map(mapEventFromDb)
          : [];
        if (!cancelled) {
          setEvents(dbEvents);
        }
      } catch (error) {
        if (!cancelled) {
          setEvents([]);
        }
      }
    };

    loadEvents();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const checkLiveStatus = async () => {
      const apiKey = process.env.YOUTUBE_API_KEY;
      const channelId = process.env.YOUTUBE_CHANNEL_ID;

      if (!apiKey || !channelId) return;

      const apiUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&eventType=live&type=video&key=${apiKey}`;
      try {
        const response = await fetch(apiUrl);
        const data = await response.json();
        setIsLive(data.items && data.items.length > 0);
      } catch (error) {
        //console.error("YouTube API error:", error);
        setIsLive(false);
      }
    };
    checkLiveStatus();

    const intervalId = setInterval(checkLiveStatus, 600000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    getActiveRetreat();
  }, [getActiveRetreat]);

  useEffect(() => {
    const onLangChange = (lng) => setLanguage(lng.slice(0, 2));
    i18n.on("languageChanged", onLangChange);
    return () => i18n.off("languageChanged", onLangChange);
  }, []);

  const handleChangeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
    handleLinkClick();
  };



  const filterMenuByUserType = (menu, userType) => {
    if (!userType) return { items: [] };
    const userTypes = new Set(userType.split(","));

    const filteredMenu = {
      items: menu.items
        .map((item) => {
          const filteredSubItems = item.subItems
            ? item.subItems.filter((sub) => {
              if (!sub.userTypes) return true;
              const allowed = new Set(sub.userTypes.split(","));
              return [...userTypes].some((type) => allowed.has(type));
              /* console.log(
            `Subitem ${subItem.name} is ${
              isVisible ? "visible" : "hidden"
            } for user types ${userType}`
          );*/
            })
            : [];

          return filteredSubItems.length
            ? { ...item, subItems: filteredSubItems }
            : null;
        })
        .filter(Boolean),
    };
    return filteredMenu;
  };

  const menuData = useMemo(() => {
    const menuMap = {
      en: guestMenuEn.items,
      es: guestMenuEs.items,
      sv: guestMenuSv.items,
      de: guestMenuDe.items,
    };
    const currentLang = i18n.language.slice(0, 2);
    const guestMenu = menuMap[currentLang] || guestMenuEn.items;
    const menuItems = [...guestMenu];

    /**/

    if (activeRetreat) {
      menuItems.forEach((item) => {
        if (item.name === "Schedules" || item.name === "Horarios") {
          Object.assign(item, {
            glow: true,
            color: "orange",
            live: true,
            bold: true,
          });
        }
      });
      menuItems.push({
        name: `${activeRetreat.name.substring(0, 23)}`, // Set fixed length
        link: `/retreat-registration?id=${activeRetreat.id}`,
        type: "single",
        live: true,
        bold: true,
        glow: true,
        color: "orange", // Add orange color
      });
    } else {
      menuItems.push({
        name: "Live",
        link:
          process.env.REACT_APP_YOUTUBE_LIVE_LINK ||
          "https://www.youtube.com/@FRDOMINICVALANMANALOFFICIAL/Live",
        type: "single",
        live: isLive,
        external: true,
      });
    }

    if (user) {
      const userFilteredAdminMenu = filterMenuByUserType(adminMenu, userType);
      menuItems.push(...userFilteredAdminMenu.items);
    }
    return { items: menuItems };
  }, [user, userType, isLive, activeRetreat, language]);

  const handleToggle = () => setIsExpanded(!isExpanded);
  const handleLinkClick = () => setIsExpanded(false);

  if (loading) {
    return (
      <nav className="navbar navbar-expand-lg navbar-light px-4 py-2">
        <div className="container-fluid justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar navbar-expand-lg navbar-light px-4 py-2">
      <div className="container-fluid">
        {/* Logo */}
        <div className="d-flex align-items-center">
          <MarianLogo />
        </div>

        <button
          className="navbar-toggler"
          type="button"
          onClick={handleToggle}
          aria-controls="navbarCollapse"
          aria-expanded={isExpanded}
          aria-label="Toggle Menu"
        >
          <FontAwesomeIcon icon={isExpanded ? faTimes : faBars} />
        </button>

        {/* Collapsible Content */}
        <div
          className={`collapse navbar-collapse ${isExpanded ? "show" : ""}`}
          id="navbarCollapse"
        >
          <div className="navbar-nav ms-auto gap-2">
            {/* Main Menu Items */}
            {menuData.items.map((item) =>
              item.type === "single" ? (
                <a
                  href={item.link}
                  key={item.name}
                  className={`nav-item nav-link ${item.live ? "blink" : ""}`}
                  target={item.external ? "_blank" : "_self"}
                  rel={item.external ? "noopener noreferrer" : ""}
                  onClick={handleLinkClick}
                  style={{
                    fontWeight: item.bold ? "bold" : "normal",
                    textShadow: item.glow ? "0 0 8px orange" : "none",
                  }}
                >
                  {item.name}
                </a>
              ) : (
                <div className="nav-item dropdown" key={item.name}>
                  <a
                    href="/"
                    className={`nav-link dropdown-toggle ${item.live ? "blink" : ""
                      }`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{
                      fontWeight: item.bold ? "bold" : "normal",
                      textShadow: item.glow ? "0 0 8px orange" : "none",
                    }}
                    onClick={(e) => e.preventDefault()}
                  >
                    {item.name}
                  </a>
                  <ul className="dropdown-menu m-0">
                    {item.subItems.map((subItem) => (
                      <li key={subItem.name}>
                        <a
                          href={subItem.link}
                          className="dropdown-item"
                          onClick={handleLinkClick}
                          target={subItem.external ? "_blank" : "_self"}
                          rel={subItem.external ? "noopener noreferrer" : ""}
                        >
                          {subItem.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {/* Login / Logout */}
            {user ? (
              <a
                href="/logout"
                className="nav-item nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                }}
              >
                Logout
              </a>
            ) : (
              <div className="nav-item dropdown">
                <a
                  href="/"
                  className="nav-link dropdown-toggle"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  onClick={(e) => e.preventDefault()}
                >
                  Login
                </a>
                <ul className="dropdown-menu m-0">
                  <li>
                    <a
                      href="/login"
                      className="dropdown-item"
                      onClick={handleLinkClick}
                    >
                      Login
                    </a>
                  </li>
                  <li>
                    <a
                      href="/signup"
                      className="dropdown-item"
                      onClick={handleLinkClick}
                    >
                      Sign Up
                    </a>
                  </li>
                  <li>
                    <a
                      href="/forgotPassword"
                      className="dropdown-item"
                      onClick={handleLinkClick}
                    >
                      Forgot Password
                    </a>
                  </li>
                </ul>
              </div>
            )}

            {/* Language Selector */}
            <div className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-label="Switch Language"
                onClick={(e) => e.preventDefault()}
              >
                <FontAwesomeIcon icon={faGlobe} className="me-1" />
                {language.toUpperCase()}
              </a>
              <ul className="dropdown-menu dropdown-menu-end">
                {LANGUAGES.map((lang) => (
                  <li key={lang.value}>
                    <a
                      role="button"
                      tabIndex="0"
                      className="dropdown-item"
                      onClick={(e) => {
                        //e.preventDefault();
                        handleChangeLanguage(lang.value);
                      }}
                    >
                      {lang.value.toUpperCase()}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default MenuBar;
